import { supabase } from '../lib/supabase';

export const trialService = {
  /**
   * Check if user's trial is active
   */
  async checkTrialStatus(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('trial_start_date, trial_end_date, is_trial_active, trial_converted_at')?.eq('id', userId)?.single();

      if (error) throw error;

      const now = new Date();
      const trialEnd = data?.trial_end_date ? new Date(data.trial_end_date) : null;
      const daysRemaining = trialEnd ? Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)) : 0;

      return {
        isTrialActive: data?.is_trial_active && daysRemaining > 0,
        trialStartDate: data?.trial_start_date,
        trialEndDate: data?.trial_end_date,
        daysRemaining: Math.max(0, daysRemaining),
        isExpired: trialEnd && trialEnd < now,
        hasConverted: !!data?.trial_converted_at
      };
    } catch (error) {
      console.error('Error checking trial status:', error);
      throw error;
    }
  },

  /**
   * Convert trial to paid subscription
   */
  async convertTrialToPaid(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          is_trial_active: false,
          trial_converted_at: new Date()?.toISOString()
        })?.eq('id', userId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error converting trial:', error);
      throw error;
    }
  },

  /**
   * Send account activation email
   */
  async sendActivationEmail(email, fullName, trialEndDate) {
    try {
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-account-activation-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            email,
            fullName,
            trialEndDate
          })
        }
      );

      if (!response?.ok) {
        let errorMessage = 'Failed to send activation email';
        try {
          const errorData = await response?.json();
          errorMessage = errorData?.error || errorData?.message || errorMessage;
          
          // Add more context based on status code
          if (response?.status === 500) {
            errorMessage = `Email service error: ${errorMessage}`;
          } else if (response?.status === 400) {
            errorMessage = `Invalid email data: ${errorMessage}`;
          }
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = `${response?.status} - ${response?.statusText || errorMessage}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response?.json();
      return result;
    } catch (error) {
      console.error('Error sending activation email:', error);
      
      // Provide more specific error messages
      if (error?.message?.includes('fetch') || error?.name === 'TypeError') {
        throw new Error('Network error: Unable to reach email service. Please check your connection.');
      }
      
      if (error?.message?.includes('CORS')) {
        throw new Error('Email service configuration error. Please contact support.');
      }
      
      // Re-throw the original error with context
      throw new Error(`Email delivery failed: ${error?.message}`);
    }
  }
};